/**
 * Seller Verification Page - Redesigned with EntityManagement design system
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip } from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DescriptionIcon from '@mui/icons-material/Description';
import InboxIcon from '@mui/icons-material/Inbox';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BlockIcon from '@mui/icons-material/Block';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import NoPhotographyIcon from '@mui/icons-material/NoPhotography';
import moment from 'moment-jalaali';
import sellerVerificationService, { SellerVerification } from '../services/sellerVerificationService';
import MyShiftStatusBanner from '../components/common/MyShiftStatusBanner';
import { toPersianDigits } from '../components/common/JalaliDatePicker';

import './EntityManagement.css';
import AssigneeCell from '../components/common/AssigneeCell';

type VerificationStatus = 'pending' | 'approved' | 'rejected' | 'needs_revision';
type ReviewAction = 'approve' | 'reject' | 'needs_revision';

const statusLabels: Record<string, string> = {
  pending: 'در انتظار بررسی',
  approved: 'تایید شده',
  rejected: 'رد شده',
  needs_revision: 'نیاز به اصلاح'
};

const statusTagClass: Record<string, string> = {
  pending: 'tag tag-sm tag-warning',
  approved: 'tag tag-sm tag-success',
  rejected: 'tag tag-sm tag-danger',
  needs_revision: 'tag tag-sm tag-active'
};

const formatJalali = (d?: string) => {
  if (!d) return '-';
  const m = moment(d);
  if (!m.isValid()) return '-';
  return toPersianDigits(m.format('jYYYY/jMM/jDD HH:mm'));
};

const SellerVerificationPage: React.FC = () => {
  const [verifications, setVerifications] = useState<SellerVerification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterStatus, setFilterStatus] = useState<VerificationStatus | ''>('pending');

  // Stats
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });

  // Dialogs
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>('');
  const [currentVerification, setCurrentVerification] = useState<SellerVerification | null>(null);
  const [reviewAction, setReviewAction] = useState<ReviewAction>('approve');
  const [reviewReason, setReviewReason] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [operationSuccess, setOperationSuccess] = useState<string | null>(null);

  const fetchVerifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await sellerVerificationService.getVerifications(page + 1, rowsPerPage, {
        status: filterStatus || undefined
      });
      let items = response.items || [];
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        items = items.filter((v) =>
          (v.userId?.name || '').toLowerCase().includes(q) ||
          (v.userId?.email || '').toLowerCase().includes(q) ||
          (v.storeId?.name || '').toLowerCase().includes(q) ||
          (v.nationalId || '').toLowerCase().includes(q)
        );
      }
      setVerifications(items);
      setTotalItems(response.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در بارگذاری اطلاعات');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filterStatus, searchTerm]);

  const fetchStats = useCallback(async () => {
    try {
      const [all, pending, approved, rejected] = await Promise.all([
        sellerVerificationService.getVerifications(1, 1, {}),
        sellerVerificationService.getVerifications(1, 1, { status: 'pending' }),
        sellerVerificationService.getVerifications(1, 1, { status: 'approved' }),
        sellerVerificationService.getVerifications(1, 1, { status: 'rejected' })
      ]);
      setStats({
        total: all.total || 0,
        pending: pending.total || 0,
        approved: approved.total || 0,
        rejected: rejected.total || 0
      });
    } catch (e) {
      /* noop */
    }
  }, []);

  useEffect(() => { fetchVerifications(); }, [fetchVerifications]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleSearch = () => { setSearchTerm(searchInput); setPage(0); };
  const handleKeyPress = (e: React.KeyboardEvent) => { if (e.key === 'Enter') handleSearch(); };

  const openDetails = (v: SellerVerification) => { setCurrentVerification(v); setDetailsOpen(true); };
  const openReview = (v: SellerVerification, action: ReviewAction) => {
    setCurrentVerification(v);
    setReviewAction(action);
    setReviewReason('');
    setOperationSuccess(null);
    setReviewOpen(true);
  };
  const closeAll = () => {
    setDetailsOpen(false);
    setReviewOpen(false);
    setCurrentVerification(null);
    setOperationSuccess(null);
  };

  const clearFilters = () => { setSearchTerm(''); setSearchInput(''); setFilterStatus(''); setPage(0); };

  const handleReview = async () => {
    if (!currentVerification) return;
    if ((reviewAction === 'reject' || reviewAction === 'needs_revision') && !reviewReason.trim()) return;

    setReviewing(true);
    try {
      await sellerVerificationService.reviewVerification(
        currentVerification._id,
        reviewAction,
        reviewReason || undefined
      );
      const messages: Record<ReviewAction, string> = {
        approve: 'درخواست تایید شد',
        reject: 'درخواست رد شد',
        needs_revision: 'درخواست به اصلاح ارسال شد'
      };
      setOperationSuccess(messages[reviewAction]);
      setTimeout(() => { closeAll(); fetchVerifications(); fetchStats(); }, 1000);
    } catch (e) {
      console.error(e);
    } finally {
      setReviewing(false);
    }
  };

  const openImagePreview = (url: string) => {
    setImagePreviewUrl(url);
    setImagePreviewOpen(true);
  };

  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const startItem = totalItems === 0 ? 0 : page * rowsPerPage + 1;
  const endItem = Math.min((page + 1) * rowsPerPage, totalItems);
  const hasActiveFilters = !!searchTerm || !!filterStatus;

  const reviewActionMeta = {
    approve: {
      title: 'تایید درخواست احراز هویت',
      iconBg: 'var(--color-success-bg)',
      iconColor: 'var(--color-success-dark)',
      icon: <CheckCircleIcon style={{ fontSize: 28, color: 'var(--color-success-dark)' }} />,
      confirmText: 'تایید',
      confirmColor: 'var(--color-success-dark)',
      confirmIcon: <CheckCircleIcon fontSize="small" />,
      successColor: 'var(--color-success-dark)'
    },
    reject: {
      title: 'رد درخواست احراز هویت',
      iconBg: 'var(--color-error-bg)',
      iconColor: 'var(--color-error-dark)',
      icon: <CancelIcon style={{ fontSize: 28, color: 'var(--color-error-dark)' }} />,
      confirmText: 'رد درخواست',
      confirmColor: 'var(--color-error-dark)',
      confirmIcon: <CancelIcon fontSize="small" />,
      successColor: 'var(--color-error-dark)'
    },
    needs_revision: {
      title: 'درخواست اصلاح مدارک',
      iconBg: 'var(--color-warning-bg)',
      iconColor: 'var(--color-warning-dark)',
      icon: <EditIcon style={{ fontSize: 28, color: 'var(--color-warning-dark)' }} />,
      confirmText: 'ارسال درخواست اصلاح',
      confirmColor: 'var(--color-warning-dark)',
      confirmIcon: <EditIcon fontSize="small" />,
      successColor: 'var(--color-warning-dark)'
    }
  } as const;

  const meta = reviewActionMeta[reviewAction];

  return (
    <div className="entity-page">
      <MyShiftStatusBanner category="seller_verification" />

      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">تأیید فروشندگان</h1>
          <p className="entity-subtitle">بررسی و تأیید مدارک فروشندگان</p>
        </div>
        <div className="header-actions">
          <button
            className="header-btn refresh"
            onClick={() => { fetchVerifications(); fetchStats(); }}
            title="بروزرسانی"
          >
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><VerifiedUserIcon /></div>
          <div>
            <p className="entity-stat-label">کل درخواست‌ها</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.total))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon orange"><HourglassEmptyIcon /></div>
          <div>
            <p className="entity-stat-label">در انتظار بررسی</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.pending))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckCircleIcon /></div>
          <div>
            <p className="entity-stat-label">تایید شده</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.approved))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon red"><BlockIcon /></div>
          <div>
            <p className="entity-stat-label">رد شده</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.rejected))}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجوی نام، ایمیل یا کد ملی..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">وضعیت</label>
            <select
              className="filter-field"
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value as VerificationStatus | ''); setPage(0); }}
            >
              <option value="">همه وضعیت‌ها</option>
              <option value="pending">در انتظار بررسی</option>
              <option value="approved">تایید شده</option>
              <option value="rejected">رد شده</option>
              <option value="needs_revision">نیاز به اصلاح</option>
            </select>
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {searchTerm && (
              <span className="filter-tag">
                جستجو: {searchTerm}
                <button onClick={() => { setSearchTerm(''); setSearchInput(''); }}>×</button>
              </span>
            )}
            {filterStatus && (
              <span className="filter-tag">
                وضعیت: {statusLabels[filterStatus]}
                <button onClick={() => setFilterStatus('')}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading && !verifications.length ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : error ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><InboxIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">{error}</p>
            <button className="header-btn primary" onClick={fetchVerifications} style={{ marginTop: 12 }}>
              <RefreshIcon fontSize="small" /> تلاش مجدد
            </button>
          </div>
        ) : verifications.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><InboxIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">درخواستی یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>فروشنده</th>
                  <th>فروشگاه</th>
                  <th>کد ملی</th>
                  <th>مدارک</th>
                  <th>وضعیت</th>
                  <th>مسئول</th>
                  <th>تاریخ ارسال</th>
                  <th style={{ width: 180 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {verifications.map((v) => (
                  <tr key={v._id}>
                    <td>
                      <div className="entity-info-cell">
                        <div className="entity-image">
                          <VerifiedUserIcon />
                        </div>
                        <div className="entity-info">
                          <span className="entity-name">{v.userId?.name || 'نامشخص'}</span>
                          <span className="entity-desc">{v.userId?.email || '-'}</span>
                        </div>
                      </div>
                    </td>
                    <td><span className="entity-desc">{v.storeId?.name || '-'}</span></td>
                    <td><span className="entity-desc">{v.nationalId ? toPersianDigits(v.nationalId) : '-'}</span></td>
                    <td>
                      <span className="tag tag-sm tag-active" style={{ cursor: 'pointer' }} onClick={() => openDetails(v)}>
                        <DescriptionIcon style={{ fontSize: 14, verticalAlign: 'middle', marginLeft: 2 }} />
                        {toPersianDigits(String(v.documents?.length || 0))} مدرک
                      </span>
                    </td>
                    <td><span className={statusTagClass[v.status]}>{statusLabels[v.status]}</span></td>
                    <td><AssigneeCell value={(v as any).assignedTo} /></td>
                    <td><span className="entity-desc">{formatJalali(v.createdAt)}</span></td>
                    <td>
                      <div className="actions-cell">
                        <Tooltip title="جزئیات" arrow>
                          <button className="action-btn edit" onClick={() => openDetails(v)}>
                            <VisibilityIcon />
                          </button>
                        </Tooltip>
                        {v.status === 'pending' && (
                          <>
                            <Tooltip title="تایید" arrow>
                              <button className="action-btn toggle active" onClick={() => openReview(v, 'approve')}>
                                <CheckCircleIcon />
                              </button>
                            </Tooltip>
                            <Tooltip title="نیاز به اصلاح" arrow>
                              <button
                                className="action-btn edit"
                                style={{ color: 'var(--color-warning-dark)' }}
                                onClick={() => openReview(v, 'needs_revision')}
                              >
                                <EditIcon />
                              </button>
                            </Tooltip>
                            <Tooltip title="رد" arrow>
                              <button className="action-btn delete" onClick={() => openReview(v, 'reject')}>
                                <CancelIcon />
                              </button>
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {verifications.map((v) => (
                <div key={v._id} className="entity-card">
                  <div className="entity-card-header">
                    <div className="entity-card-image">
                      <VerifiedUserIcon />
                    </div>
                    <div className="entity-card-info">
                      <div className="entity-card-name">{v.userId?.name || 'نامشخص'}</div>
                      <div className="entity-card-desc">{v.userId?.email || '-'}</div>
                      <div className="entity-card-desc">{v.storeId?.name || '-'}</div>
                    </div>
                    <div className="entity-card-status">
                      <span className={statusTagClass[v.status]}>{statusLabels[v.status]}</span>
                    </div>
                  </div>
                  <div className="entity-card-actions">
                    <button className="entity-card-btn primary" onClick={() => openDetails(v)}>
                      <VisibilityIcon fontSize="small" /> جزئیات
                    </button>
                    {v.status === 'pending' && (
                      <>
                        <button className="entity-card-btn" onClick={() => openReview(v, 'approve')}>
                          <CheckCircleIcon fontSize="small" /> تایید
                        </button>
                        <button className="entity-card-btn danger" onClick={() => openReview(v, 'reject')}>
                          <CancelIcon fontSize="small" /> رد
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {toPersianDigits(String(startItem))} تا {toPersianDigits(String(endItem))} از {toPersianDigits(String(totalItems))}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-select"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <button className="pagination-btn" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span style={{ fontSize: 12 }}>{toPersianDigits(String(page + 1))} / {toPersianDigits(String(totalPages || 1))}</span>
                <button className="pagination-btn" onClick={() => setPage(page - 1)} disabled={page <= 0}>
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={closeAll} maxWidth="md" fullWidth className="sellerverify-details-dialog">
        <div className="edit-dialog-title">
          <span>جزئیات درخواست احراز هویت</span>
          <button className="details-close" onClick={closeAll}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {currentVerification && (
            <div className="edit-form">
              {/* Seller Info */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات فروشنده</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>نام و نام خانوادگی</label>
                      <input type="text" readOnly value={currentVerification.userId?.name || '-'} />
                    </div>
                    <div className="edit-field">
                      <label>ایمیل</label>
                      <input type="text" readOnly value={currentVerification.userId?.email || '-'} />
                    </div>
                    {currentVerification.userId?.phone && (
                      <div className="edit-field">
                        <label>تلفن</label>
                        <input type="text" readOnly value={toPersianDigits(currentVerification.userId.phone)} />
                      </div>
                    )}
                    <div className="edit-field">
                      <label>نام فروشگاه</label>
                      <input type="text" readOnly value={currentVerification.storeId?.name || '-'} />
                    </div>
                    <div className="edit-field">
                      <label>تاریخ ارسال</label>
                      <input type="text" readOnly value={formatJalali(currentVerification.createdAt)} />
                    </div>
                    {currentVerification.reviewedAt && (
                      <div className="edit-field">
                        <label>تاریخ بررسی</label>
                        <input type="text" readOnly value={formatJalali(currentVerification.reviewedAt)} />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Identity Documents */}
              <div className="edit-card">
                <div className="edit-card-header">مدارک هویتی</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>کد ملی</label>
                      <input type="text" readOnly value={currentVerification.nationalId ? toPersianDigits(currentVerification.nationalId) : '-'} />
                    </div>
                    <div className="edit-field">
                      <label>شماره مجوز کسب</label>
                      <input type="text" readOnly value={currentVerification.businessLicense ? toPersianDigits(currentVerification.businessLicense) : '-'} />
                    </div>
                  </div>

                  {currentVerification.documents && currentVerification.documents.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginTop: 16 }}>
                      {currentVerification.documents.map((doc, index) => (
                        <div
                          key={index}
                          style={{
                            border: '1px solid var(--color-border)',
                            borderRadius: 8,
                            overflow: 'hidden',
                            background: 'var(--color-bg-paper)'
                          }}
                        >
                          <div
                            style={{
                              height: 140,
                              background: 'var(--color-bg-default)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'zoom-in',
                              position: 'relative'
                            }}
                            onClick={() => openImagePreview(doc.url)}
                          >
                            <img
                              src={doc.url}
                              alt={doc.type}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                            <div
                              style={{
                                position: 'absolute',
                                top: 6,
                                left: 6,
                                background: 'rgba(0,0,0,0.55)',
                                color: '#fff',
                                borderRadius: 4,
                                padding: '2px 6px',
                                fontSize: 11,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                              }}
                            >
                              <ZoomInIcon style={{ fontSize: 14 }} />
                              بزرگنمایی
                            </div>
                          </div>
                          <div style={{ padding: 8, fontSize: 12 }}>
                            <div style={{ fontWeight: 600 }}>{doc.type}</div>
                            <div style={{ color: 'var(--color-text-secondary)', fontSize: 11, marginTop: 2 }}>
                              {formatJalali(doc.uploadedAt)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: 24, color: 'var(--color-text-disabled)' }}>
                      <NoPhotographyIcon style={{ fontSize: 40 }} />
                      <span style={{ fontSize: 13 }}>مدرکی آپلود نشده است</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Verification Decision */}
              <div className="edit-card">
                <div className="edit-card-header">وضعیت بررسی</div>
                <div className="edit-card-body">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>وضعیت فعلی:</span>
                      <span className={statusTagClass[currentVerification.status]}>{statusLabels[currentVerification.status]}</span>
                    </div>
                    {currentVerification.reviewedBy?.name && (
                      <div className="edit-field">
                        <label>بررسی شده توسط</label>
                        <input type="text" readOnly value={currentVerification.reviewedBy.name} />
                      </div>
                    )}
                    {currentVerification.rejectionReason && (
                      <div className="edit-field">
                        <label>دلیل / توضیحات</label>
                        <textarea readOnly value={currentVerification.rejectionReason} rows={3} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={closeAll}>بستن</button>
          {currentVerification?.status === 'pending' && (
            <>
              <button
                className="edit-btn cancel"
                style={{ color: 'var(--color-error-dark)', borderColor: 'var(--color-error-dark)' }}
                onClick={() => { setDetailsOpen(false); openReview(currentVerification, 'reject'); }}
              >
                <CancelIcon fontSize="small" /> رد درخواست
              </button>
              <button
                className="edit-btn cancel"
                style={{ color: 'var(--color-warning-dark)', borderColor: 'var(--color-warning-dark)' }}
                onClick={() => { setDetailsOpen(false); openReview(currentVerification, 'needs_revision'); }}
              >
                <EditIcon fontSize="small" /> نیاز به اصلاح
              </button>
              <button
                className="edit-btn submit"
                style={{ background: 'var(--color-success-dark)' }}
                onClick={() => { setDetailsOpen(false); openReview(currentVerification, 'approve'); }}
              >
                <CheckCircleIcon fontSize="small" /> تایید درخواست
              </button>
            </>
          )}
        </div>
      </Dialog>

      {/* Review Confirmation Dialog */}
      <Dialog open={reviewOpen} onClose={closeAll} maxWidth="xs" fullWidth className="sellerverify-confirm-dialog">
        <div className="delete-dialog-content">
          <div className="delete-dialog-icon" style={{ background: meta.iconBg }}>
            {meta.icon}
          </div>
          <h3 className="delete-dialog-title">{meta.title}</h3>
          {operationSuccess ? (
            <p className="delete-dialog-text" style={{ color: meta.successColor }}>
              <CheckCircleIcon style={{ fontSize: 18, verticalAlign: 'middle', marginLeft: 4 }} />
              {operationSuccess}
            </p>
          ) : (
            <>
              <p className="delete-dialog-text">
                {reviewAction === 'approve' ? (
                  <>
                    آیا از تایید درخواست احراز هویت <strong>«{currentVerification?.userId?.name || 'نامشخص'}»</strong> اطمینان دارید؟
                    <br />با تایید، فروشنده تأیید شده می‌شود.
                  </>
                ) : reviewAction === 'reject' ? (
                  <>
                    دلیل رد درخواست <strong>«{currentVerification?.userId?.name || 'نامشخص'}»</strong> را وارد کنید.
                    <br />این دلیل به کاربر نمایش داده می‌شود.
                  </>
                ) : (
                  <>
                    موارد نیازمند اصلاح در درخواست <strong>«{currentVerification?.userId?.name || 'نامشخص'}»</strong> را وارد کنید.
                    <br />این توضیحات به کاربر نمایش داده می‌شود.
                  </>
                )}
              </p>
              {(reviewAction === 'reject' || reviewAction === 'needs_revision') && (
                <div className="edit-field" style={{ textAlign: 'right', margin: '0 0 16px' }}>
                  <textarea
                    value={reviewReason}
                    onChange={(e) => setReviewReason(e.target.value)}
                    placeholder={reviewAction === 'reject' ? 'دلیل رد را وارد کنید...' : 'موارد نیازمند اصلاح را وارد کنید...'}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: `1px solid ${!reviewReason.trim() ? meta.confirmColor : 'var(--color-border)'}`,
                      borderRadius: 8,
                      fontFamily: 'inherit',
                      fontSize: '0.875rem',
                      resize: 'vertical',
                      boxSizing: 'border-box',
                      background: 'var(--color-bg-paper)',
                      color: 'var(--color-text-primary)'
                    }}
                  />
                  {!reviewReason.trim() && (
                    <span style={{ color: meta.confirmColor, fontSize: 12, marginTop: 4, display: 'block' }}>
                      وارد کردن توضیحات الزامی است
                    </span>
                  )}
                </div>
              )}
            </>
          )}
          <div className="delete-dialog-actions">
            <button className="btn btn-ghost" onClick={closeAll} disabled={reviewing}>انصراف</button>
            <button
              className="btn btn-danger"
              style={{ background: meta.confirmColor }}
              onClick={handleReview}
              disabled={
                reviewing ||
                !!operationSuccess ||
                ((reviewAction === 'reject' || reviewAction === 'needs_revision') && !reviewReason.trim())
              }
            >
              {reviewing && <CircularProgress size={16} style={{ color: '#fff' }} />}
              {meta.confirmIcon} {meta.confirmText}
            </button>
          </div>
        </div>
      </Dialog>

      {/* Image Preview Overlay */}
      {imagePreviewOpen && imagePreviewUrl && (
        <div
          onClick={() => setImagePreviewOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out'
          }}
        >
          <button
            onClick={() => setImagePreviewOpen(false)}
            style={{
              position: 'absolute',
              top: 16,
              left: 16,
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: 'none',
              background: 'rgba(255,255,255,0.15)',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CloseIcon />
          </button>
          <img
            src={imagePreviewUrl}
            alt="مدرک"
            style={{ maxWidth: '92vw', maxHeight: '92vh', objectFit: 'contain', borderRadius: 8 }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default SellerVerificationPage;
